import { FC, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import ButtonBase from 'src/components/Button';
import ModalLayoutBase, { MobileModalType, ModalContent, ModalFooter as ModalFooterBase, ModalTitleContainer as ModalTitleContainerBase, ModalTitle as ModalTitleBase, ModalTitleDescription } from 'src/components/ModalLayout';
import { setFeatureSettingsAtom } from 'src/stores/user/atom';
import { UserSettingType } from 'src/stores/user/types';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
interface AssistantConsentLeaveModalProps {
  onBack: () => void;
  onLeave: () => void;
}
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
`;
const ModalTitleContainer = styled(ModalTitleContainerBase)`
  padding-bottom: 8px;
`;
const ModalTitle = styled(ModalTitleBase)`
  font-size: 18px;
  font-weight: 900;
  line-height: 23px;
`;
const Description = styled(ModalTitleDescription)`
  font-size: 14px;
  line-height: 18px;
  color: ${({
  theme
}) => theme.color.content9};
`;
const ModalFooter = styled(ModalFooterBase)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
`;
const Button = styled(ButtonBase)`
  width: auto;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
  }
`;

/**
 * 어시스턴트 동의 모달에서 Skip 버튼을 눌렀을 때, 다시 한번 더 확인하는 넛징 모달
 */
const AssistantConsentLeaveModal: FC<AssistantConsentLeaveModalProps> = ({
  onBack,
  onLeave
}) => {
  const {
    t
  } = useTranslation();
  const setFeatureSettings = useSetAtom(setFeatureSettingsAtom);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ASSISTANT_CONSENT_POPUP__IMPRESSION_MISS_OUT_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: 'assistant_consent_popup',
        target: 'miss_out_popup'
      }
    });
  }, []);
  const handleLeave = useCallback(async () => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ASSISTANT_CONSENT_POPUP__CLICK_LEAVE_NOW,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'assistant_consent_popup',
        target: 'leave_now'
      }
    });
    await setFeatureSettings([{
      type: UserSettingType.ASSISTANT_CONSENTED,
      value: false
    }, {
      type: UserSettingType.ASSISTANT_ENABLED,
      value: false
    }]);
    onLeave();
  }, [setFeatureSettings, onLeave]);
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='AssistantConsentLeaveModal' data-sentry-element="ModalLayout" data-sentry-component="AssistantConsentLeaveModal" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
      <ModalTitleContainer data-sentry-element="ModalTitleContainer" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="AssistantConsentLeaveModal.tsx">{t('azar_assistant.consent_popup_exit_title')}</ModalTitle>
      </ModalTitleContainer>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
        <Description dangerouslySetInnerHTML={{
        __html: t('azar_assistant.consent_popup_exit_desc')
      }} data-sentry-element="Description" data-sentry-source-file="AssistantConsentLeaveModal.tsx" />
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
        <Button id='assistant-consent-leave-back' color='tertiary' onClick={onBack} data-testid='AssistantConsentLeaveModal-button-back' data-sentry-element="Button" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
          {t('COMMON__CANCEL')}
        </Button>
        <Button id='assistant-consent-leave-now' color='secondary' onClick={handleLeave} data-testid='AssistantConsentLeaveModal-button-leave' data-sentry-element="Button" data-sentry-source-file="AssistantConsentLeaveModal.tsx">
          {t('azar_assistant.consent_popup_exit')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
export default AssistantConsentLeaveModal;